/**
 * The trial window and its one outcome (APP-ENTITLEMENT-001, Q-AE-19): fourteen whole days from the start, no
 * card, and at the end the app turns READ-ONLY — nothing deleted, nothing charged. It converts only through an
 * explicit confirmation with a card on file, which is the billing engine's write, never this rule's.
 *
 * Instants are ISO strings with an offset (TS-001): this package never hands a `Date` across a boundary.
 *
 * @module @puffin-core/app-catalog/rules/trial
 */

'use strict';

const { ENTITLEMENT_STATUS, TRIAL_DAYS } = require('../vocabulary');

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toInstant(value, name) {
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  if (!Number.isFinite(ms)) {
    throw new TypeError(`${name} must be an ISO instant or a Date, got ${JSON.stringify(value)}`);
  }
  return ms;
}

/**
 * The trial window that starts at `now`.
 *
 * @param {string|Date} now
 * @param {number} [trialDays=TRIAL_DAYS] - a positive whole number of days.
 * @returns {{trialStartedAt: string, trialEndsAt: string, trialDays: number}} ISO instants (UTC).
 */
function trialWindowFrom(now, trialDays = TRIAL_DAYS) {
  if (!Number.isInteger(trialDays) || trialDays <= 0) {
    throw new TypeError(`trialDays must be a positive integer, got ${JSON.stringify(trialDays)}`);
  }
  const start = toInstant(now, 'now');
  return {
    trialStartedAt: new Date(start).toISOString(),
    trialEndsAt: new Date(start + trialDays * MS_PER_DAY).toISOString(),
    trialDays,
  };
}

/**
 * The status a trialing entitlement should take at `now`: `read_only` once its end instant has passed (at the
 * instant itself, never before), otherwise null — nothing to do. A non-trialing entitlement never changes here.
 *
 * @param {{status: string, trialEndsAt?: ?string}} entitlement
 * @param {string|Date} now
 * @returns {?string} `'read_only'` or null.
 */
function trialOutcomeAt(entitlement, now) {
  if (entitlement.status !== ENTITLEMENT_STATUS.TRIALING) return null;
  if (entitlement.trialEndsAt === null || entitlement.trialEndsAt === undefined) {
    throw new TypeError('a trialing entitlement must carry trialEndsAt');
  }
  const endsAt = toInstant(entitlement.trialEndsAt, 'trialEndsAt');
  return toInstant(now, 'now') >= endsAt ? ENTITLEMENT_STATUS.READ_ONLY : null;
}

/**
 * Whole days left in a trial at `now` (0 at or after the end), for the tile and the reminder mails.
 *
 * @param {{trialEndsAt: string}} entitlement
 * @param {string|Date} now
 * @returns {number}
 */
function trialDaysLeft(entitlement, now) {
  const remaining = toInstant(entitlement.trialEndsAt, 'trialEndsAt') - toInstant(now, 'now');
  return remaining <= 0 ? 0 : Math.ceil(remaining / MS_PER_DAY);
}

module.exports = { trialWindowFrom, trialOutcomeAt, trialDaysLeft };
